import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import {AppState} from '../store';
import {createSelector, select, Store} from '@ngrx/store';
import {selectPaypalConfig, selectTenantInfo} from '../store/selectors';
import {PaypalConfig} from '../models/paypal-config.model';
import {environment} from '../../environments/environment';
import {payPalScriptLoadRequested} from '../store/platform.actions';
import {checkIfPlatformSite} from '../common/platform-utils';
import {ActivatedRoute} from '@angular/router';
import {toSignal} from '@angular/core/rxjs-interop';

const selectPaypalIntegrationData = createSelector(
  selectPaypalConfig,
  selectTenantInfo,
  (paypalConfig, tenant) => ({ paypalConfig, tenant })
);

@Component({
    selector: "paypal-integration",
    template: "",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaypalIntegrationComponent {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly route = inject(ActivatedRoute);

  private readonly paypalData = toSignal(this.store.pipe(select(selectPaypalIntegrationData)));
  private readonly hasActivated = signal(false);

  constructor() {
    effect(() => {
      const data = this.paypalData();
      if (!this.hasActivated() && data?.paypalConfig?.enabled && !!data?.paypalConfig?.paypalMerchantId) {
        this.hasActivated.set(true);
        this.activatePaypalIntegration(data.paypalConfig, data?.tenant?.currency);
      }
    });
  }

  activatePaypalIntegration(config: PaypalConfig, currency: string) {
    if (!checkIfPlatformSite()) {
      let paypalPlatformClientId = environment.paypal.platformClientId;

      const testPaypalPlatformId = this.route.snapshot.queryParams['testPaypalPlatformId'];
      if (testPaypalPlatformId) {
        paypalPlatformClientId = testPaypalPlatformId;
      }

      var paypalScript = document.createElement('script');
      paypalScript.type = 'text/javascript';
      paypalScript.src = `https://www.paypal.com/sdk/js?client-id=${paypalPlatformClientId}&merchant-id=${config.paypalMerchantId}&currency=${currency}`;
      paypalScript.onerror = () => {
        console.error("PayPal script failed to load. Check integration parameters (e.g. Merchant Id) in Admin → Settings → Integrations.");
      };
      var head = document.getElementsByTagName('head')[0];
      head.appendChild(paypalScript);

      console.log("Activating Paypal integration");
      this.store.dispatch(payPalScriptLoadRequested());
    }
  }
}
